import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import numeral from 'numeral';
import 'numeral/locales/ru';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

import {
  editCurrentSupply,
  confirmSupply,
  confirmSupplyDifference,
  completedSupply,
  returnToCompletionSupply,
  fetchGetPurchaseOrder,
} from '../../../../actions/supplies';
import { fetchImportParsedProducts, fetchItemsTemplate } from '../../../../actions/imports';
import { showModal } from '../../../../actions/modal';

import { parseError, getNdsValue, saveOctetStreamAsFile } from '../../../../helpers';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';
import Adaptive from '../../../common/Adaptive';
import { SUPPLIES_TABS_NAME } from '../../../../config';

numeral.locale('ru');

const getTotalNds = (items) => {
  return items.reduce((a, c) => {
    const ndsValue = getNdsValue(c.vatRate);
    if (ndsValue !== null) {
      a += (c.price * c.quantity * ndsValue) / 100;
    }
    return a;
  }, 0);
};

const ItemList = (props) => {
  const { currentWarehouse, handleSaveSupply, setQuantityFactChange } = props;

  const dispatch = useDispatch();
  const currentSupply = useSelector((state) => state.supplies.currentSupply);
  const currentSupplyUnsaved = useSelector((state) => state.supplies.currentSupplyUnsaved);
  const [showActions, setShowActions] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [batchAccInfoItemIndex, setBatchAccInfoItemIndex] = useState(null);
  const [activeTab, setActiveTab] = useState(SUPPLIES_TABS_NAME.PLAN);

  const defaultReserve = useSelector(
    (state) => state.settings.checkoutSettings?.defaultReserveOrders,
  );

  const isManualSupply = currentWarehouse && currentWarehouse.operatorType === 'MANUAL';
  const isSupplyClosed =
    currentSupply.status === 'COMPLETED' ||
    currentSupply.status === 'COMPLETED_WITH_DIFFERENCE' ||
    currentSupply.status === 'CANCELED' ||
    currentSupply.status === 'DIFFERENCE';

  const shouldDisplayAddSupply =
    currentSupply.status === 'DRAFT' ||
    currentSupply.status === 'NEW' ||
    currentSupply.status === 'READY_TO_TAKE_IN';

  const handleAddItems = (result, additionalData = {}, isExcelImport = false) => {
    //Многие поля и значения в функции указаны через || потому что функция применяется для добавления товаров как при импорте так и вручную из попапа. А входной массив result при этом отличается. После добавления вручную поставка автоматически не сохраняется. Поэтому там важно задать максимальное количество полей, чтобы она нормально смотрелась. А после импорта - сохранеяется, но части используемых полей в файле нет. Но после сохраения они будут возвращены API.
    const resultCache = result.slice();

    const getPriceFromCurrentSupply = (idx) => {
      if (currentSupply.planItems?.[idx]) {
        return currentSupply.planItems?.[idx].price;
      } else {
        return 0;
      }
    };

    const newItems = [];
    if (currentSupply.planItems) {
      currentSupply.planItems.forEach((p) => {
        const findCurrentProductIndexInResultCache = resultCache.findIndex(
          (i) =>
            (i.productId || i.id) === p.productId && i.variantId === p.variantId && !p.orderDocId,
        );

        if (findCurrentProductIndexInResultCache === -1) {
          newItems.push({ ...p, num: newItems.length + 1 });
        } else {
          const findCurrentProduct = { ...resultCache[findCurrentProductIndexInResultCache] };
          const currentVariant =
            findCurrentProduct.variants &&
            findCurrentProduct.variants.find((v) => v.id === p.variantId);
          if (findCurrentProduct.quantity) {
            newItems.push({
              num: newItems.length + 1,
              variantId: findCurrentProduct.variantId,
              variantExtId: findCurrentProduct.variantExtId,
              productId: findCurrentProduct.id,
              productExtId: findCurrentProduct.extId,
              name: findCurrentProduct.shortName
                ? `${findCurrentProduct.shortName} ${
                    (currentVariant &&
                      currentVariant.optionsUsed &&
                      currentVariant.optionsUsed?.map((o) => o.stringValue).join(' ')) ||
                    ''
                  }`
                : findCurrentProduct.name,
              batchAccountingAttributes: p.batchAccountingAttributes,
              quantity: findCurrentProduct.quantity,
              quantityRaw: p.quantityRaw,
              price: getPriceFromCurrentSupply(newItems.length),
              img: p.img,
              vatRate: findCurrentProduct.customVat
                ? findCurrentProduct.customVat
                : currentVariant
                ? currentVariant.vat
                : p.vatRate
                ? p.vatRate
                : 'NO_VAT',
              article: (currentVariant && currentVariant.article) || findCurrentProduct.article,
              barcode: (currentVariant && currentVariant.barcode) || findCurrentProduct.barcode,
              skuType: (currentVariant && currentVariant.type) || findCurrentProduct.type,
              orderDocId: findCurrentProduct.orderDocId || null,
              accountingAttributeTitles: findCurrentProduct.accountingAttributeTitles,
              packingMaterials: findCurrentProduct.packingMaterials,
              shortName: findCurrentProduct?.shortName,
            });
          }
          resultCache.splice(findCurrentProductIndexInResultCache, 1);
        }
      });
    }

    if (resultCache.length) {
      resultCache.forEach((p) => {
        const currentVariant = p.variants && p.variants.find((v) => v.id === p.variantId);
        if (p.quantity) {
          newItems.push({
            num: newItems.length + 1,
            variantId: p.variantId,
            variantExtId: p.variantExtId,
            productId: p.id,
            productExtId: p.extId,
            name: p.shortName
              ? `${p.shortName} ${
                  (currentVariant &&
                    currentVariant.optionsUsed &&
                    currentVariant.optionsUsed?.map((o) => o.stringValue).join(' ')) ||
                  ''
                }`
              : p.name,
            quantity: p.quantity,
            quantityRaw: 0,
            price: isExcelImport && p.price ? p.price : 0,
            batchAccountingAttributes: p.batchAccountingAttributes,
            img:
              currentVariant &&
              currentVariant.images &&
              currentVariant.images.length &&
              currentVariant.images[0].url,
            vatRate: p.vatRate || 'NO_VAT',
            article: (currentVariant && currentVariant.article) || p.article,
            barcode: (currentVariant && currentVariant.barcode) || p.barcode,
            skuType: (currentVariant && currentVariant.type) || p.type,
            orderDocId: p.orderDocId || null,
            accountingAttributeTitles: p.accountingAttributeTitles,
            packingMaterials: p.packingMaterials,
            shortName: currentVariant?.shortName || p?.shortName,
          });
        }
      });
    }

    dispatch(
      editCurrentSupply({
        ...currentSupply,
        planItems: newItems,
        ...additionalData,
      }),
    );
    return newItems;
  };

  const handleAddItemsManually = () => {
    dispatch(
      showModal('SupplyAddingProducts', {
        currentProducts: currentSupply.planItems,
        currentWarehouse,
        needVariants: true,
        onProductsAdding: handleAddItems,
      }),
    );
  };

  const handleConfirmSupply = () => {
    dispatch(confirmSupply()).catch((err) => {
      toast.error(parseError(err));
      throw err;
    });
  };

  const handleConfirmSupplyDifference = () => {
    dispatch(confirmSupplyDifference()).catch((err) => {
      toast.error(parseError(err));
      throw err;
    });
  };

  const doCompletedSupply = () => {
    dispatch(completedSupply()).catch((err) => {
      toast.error(parseError(err));
      throw err;
    });
  };

  const handleCmpletedSupply = () => {
    const hasDefference =
      currentSupply.planItems &&
      currentSupply.planItems.find((item) => (item.quantity || 0) !== (item.quantityRaw || 0));

    if (hasDefference) {
      dispatch(
        showModal('Alert', {
          title: 'В поставке обнаружены расхождения',
          body: 'Фактически принятое количество расходится с плановым, поставка будет принята с расхождениями',

          handleConfirm: async () => {
            doCompletedSupply();
          },

          confirmBtnTitle: 'Принять с расхождениями',
        }),
      );
    } else {
      doCompletedSupply();
    }
  };

  const handleReturnToCompletionSupply = () => {
    dispatch(returnToCompletionSupply()).catch((err) => {
      toast.error(parseError(err));
      throw err;
    });
  };

  const handleLoadImportTemplate = () => {
    const query = '?from=purchase';
    return dispatch(fetchItemsTemplate(query)).then((res) => {
      saveOctetStreamAsFile({
        content: res.data,
        fileName: 'productsTemplate.xls',
      });
    });
  };

  const handleImport = async (file, searchColumnType) => {
    /*if (!currentSupply.supplier || !currentSupply.supplier.id || !currentSupply.warehouseCode) {
        dispatch(showModal('Message', {
            title: 'Ошибка',
            body: 'Перед импортом товаров пожалуйста выберите склад и поставщика'
        }));
        return;
    }*/
    const query = '?from=purchase';
    const res = await dispatch(fetchImportParsedProducts(file, searchColumnType, query));

    const isExcelImport = true;

    const addItemsResult = handleAddItems(res.items, { searchColumnType }, isExcelImport);

    if (addItemsResult.length) {
      try {
        await handleSaveSupply();
      } catch (e) {
        if (!res.errors) {
          res.errors = e?.data?.errors || [];
        } else {
          res.errors = [...res.errors, e?.data?.errors || []];
        }
      }
    }

    return res;
  };

  const handleImportItems = () => {
    dispatch(
      showModal('ImportProductsFromExcel', {
        handleImport,
        handleLoadImportTemplate,
      }),
    );
  };

  //Вычисляем итоговую сумму а не бреем готовую из API поскольку могут создаваться/удаляться/редактироваться записи, но это может быть еще не сохранено в API
  let total = {
    totalOrderSum: 0,
    totalVat: 0,
    totalQuantity: 0,
    factTotalOrderSum: 0,
    factTotalVat: 0,
    factTotalQuantity: 0,
  };
  if (currentSupply) {
    total = currentSupply?.planItems?.reduce((total, item) => {
      const { quantity, multiplicity } = item;

      if (item?.price) {
        total.totalOrderSum += item.price * quantity;
      }

      total.totalVat +=
        ((item.price * quantity) / (1 + (getNdsValue(item.vatRate) || 0) / 100) -
          item.price * quantity) *
        -1;

      //* Берем значение напрямую из /purchase_orders -> totalQuantity
      // if (multiplicity) {
      //   total.totalQuantity = total.totalQuantity + multiplicity * quantity;
      // } else {
      //   total.totalQuantity += quantity;
      // }

      if (currentSupply.isTakeInDiff && currentSupply.isTakeInDiffAccepted) {
        const quantity = item.quantityFact + item.quantityDefective;

        total.factTotalOrderSum += item.price * quantity;
        total.factTotalVat +=
          ((item.price * quantity) / (1 + (getNdsValue(item.vatRate) || 0) / 100) -
            item.price * quantity) *
          -1;

        if (multiplicity) {
          total.factTotalQuantity = total.factTotalQuantity + multiplicity * quantity;
        } else {
          total.factTotalQuantity += quantity;
        }
      }

      return total;
    }, total);
  }

  const orderSaved = currentSupply && currentSupply.id && !currentSupplyUnsaved;

  const handleSetFactToPlanQuantity = () => {
    const newItems =
      currentSupply.planItems &&
      currentSupply.planItems.map((item) => ({
        ...item,
        quantityRaw: item.quantity,
      }));

    dispatch(
      editCurrentSupply({
        ...currentSupply,
        items: newItems,
      }),
    );

    setQuantityFactChange(true);
    handleSaveSupply(true).then(toast.info('Фактическое количество заполнено по плановому'));
  };

  const handleExportSupply = async () => {
    try {
      const report = await dispatch(fetchGetPurchaseOrder(currentSupply.id));
      saveAs(report.fileUrl, report.fileID);
    } catch ({ data }) {
      toast.error(data.errors[0].message);
    }
  };

  const handleEditSupplyOrder = (itemIndex, ItemOrderDocId) => {
    dispatch(
      showModal('EditSupplyOrder', {
        ItemOrderDocId,
        submitCallback: ({ orderDocId, onClose }) => {
          onClose();
          const newItems = [
            ...currentSupply.planItems.slice(0, itemIndex),
            {
              ...currentSupply.planItems[itemIndex],
              orderDocId: orderDocId ? orderDocId : null,
            },
            ...currentSupply.planItems.slice(itemIndex + 1),
          ];

          dispatch(
            editCurrentSupply({
              ...currentSupply,
              items: newItems,
            }),
          );
        },
      }),
    );
  };

  const showBatchAccInfo = (itemIndex) => {
    setBatchAccInfoItemIndex(itemIndex);
  };

  const hasRelatedOrder = currentSupply?.planItems.some((item) => item.orderDocId);

  const handleSwitchTab = (value) => setActiveTab(value);

  const getSuppliesByActiveTab = () => {
    switch (activeTab) {
      case SUPPLIES_TABS_NAME.PLAN:
        return currentSupply.planItems;
      case SUPPLIES_TABS_NAME.FACT:
        return currentSupply.factItems;
      default:
        return null;
    }
  };

  const batchAccountingDataKey =
    activeTab === SUPPLIES_TABS_NAME.PLAN
      ? 'batchAccountingAttributes'
      : 'factBatchAccountingAttributes';

  const adaptiveProps = {
    orderSaved,
    currentSupply,
    isManualSupply,
    handleConfirmSupply,
    handleConfirmSupplyDifference,
    handleCmpletedSupply,
    isSupplyClosed,
    handleReturnToCompletionSupply,
    batchAccInfoItemIndex,
    showBatchAccInfo,
    setShowDropdown,
    showDropdown,
    setShowActions,
    handleExportSupply,
    handleImportItems,
    showActions,
    handleSetFactToPlanQuantity,
    currentWarehouse,
    setQuantityFactChange,
    handleAddItemsManually,
    total,
    handleEditSupplyOrder,
    defaultReserve,
    hasRelatedOrder,
    shouldDisplayAddSupply,
    activeTab,
    batchAccountingDataKey,
    handleSwitchTab,
    getSuppliesByActiveTab,
  };

  return (
    <Adaptive
      DekstopComponent={DesktopLayout}
      MobileComponent={MobileLayout}
      adaptiveProps={adaptiveProps}
    />
  );
};

export default ItemList;
